/**
  * @class msysProxy
  * @description
  * Proxy and cache service.
  * msysProxy is a special service that works as a file proxy server,
  * able to cache files of interest as configured and synchronise
  * them with msysProxy on another Mosy application.
  * This is used primarily for map tiles.
  */

import msysAbstractService from 'msysAbstractService'
const mfs = require('msysFS')
const fs = require('fs')
const mcrypt = require('msysCrypt')
const ALLOWED_MESSAGES_FROM_CLIENTS = ['mmFileListGet', 'mmFileGet']
const SERVER_LISTING_FILE = '__listing.server'
const crypto = require('crypto')

function md5File(path) {
  return new Promise((resolve, reject) => {
    const hash = crypto.createHash('md5')
    const stream = fs.createReadStream(path)
    stream.on('data', chunk => hash.update(chunk))
    stream.on('end', () => resolve(hash.digest('hex')))
    stream.on('error', reject)
  })
} // md5File

export default class msysProxy extends msysAbstractService {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union(['msysLogger'], dependencies)
    cfg_schema = _.extend({
      rootdir: { description: 'root directory where the repositories are stored', type: 'string', default: 'repos/' },
      uploaddir: {
        description: 'root directory where the repositories are stored',
        type: 'string',
        default: 'uploads',
      },
      repos: {
        description: 'definition of proxy repositories',
        type: 'object',
        patternProperties: {
          '^[\\w+\\-]+$': {
            description: 'repository',
            type: 'object',
            properties: {
              path: {
                description: 'path where the repository is stored; by default repos/<repo_name>',
                type: 'string',
              },
              fallbacks: {
                description: "fallback definitions; ':' for server, '@' for mBox, 'http://.../{path}/...' for file download",
                type: 'array',
                items: { type: 'string' },
              },
              cache: {
                description: 'whether missing items will be cached',
                type: 'boolean',
              },
              readonly: {
                description: 'do not allow any changes in the files for this repository',
                type: 'boolean',
              },
              expires: {
                description: 'how long use the cache without checking the source in seconds',
                type: 'integer',
                default: 600,
              },
              refresh: {
                description: 'periodicity of cleanup/comparison (in seconds)',
                type: 'number',
                default: 5,
              },
              pause: {
                description: 'pause between actions (in miliseconds)',
                type: 'number',
                default: 1,
              },
              pull: {
                description: 'periodicity of checking for updates on server (in seconds); if not specified, no proactive retrieval will be done',
                type: 'number',
              },
            },
          },
        },
      },
      wait_for_server: { description: 'time in ms to wait for server to be connected when using : fallback', type: 'integer', minimum: 0, default: 10000 },
      server_request_timeout: { description: 'timeout for requesting file from server', type: 'integer', minimum: 0, default: 30000 },
      memory_cache_enabled: { description: 'enable in-memory LRU cache for frequently accessed files', type: 'boolean', default: true },
      memory_cache_max_items: { description: 'maximum number of items to keep in memory cache', type: 'integer', minimum: 1, default: 200 },
      memory_cache_max_bytes: { description: 'maximum total size in bytes for memory cache (approximate)', type: 'integer', minimum: 1024, default: 20971520 },
      memory_cache_expires: { description: 'how long to keep items in memory cache without access (in seconds)', type: 'integer', minimum: 1, default: 300 },
      cache_stats_enabled: { description: 'enable in-memory cache for file stat metadata to reduce disk I/O', type: 'boolean', default: true },
      cache_stats_ttl: { description: 'how long to trust cached stat info (in milliseconds)', type: 'integer', minimum: 100, default: 2000 },
      cache_stats_max_items: { description: 'maximum number of stat entries to keep in memory', type: 'integer', minimum: 100, default: 10000 },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
    this.just_saving = {}
    this.pending_requests = new Map() // Track in-flight requests for deduplication
    this.memory_cache = new Map() // LRU cache: uri -> {data, mtime, size, last_access}
    this.memory_cache_size = 0 // Track approximate total size in bytes
    this.cache_stats = new Map() // Stat cache: uri -> {mtime, exists, checked_at}
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Received automatically (through inheritance of msysListener) when configuration and all dependencies were fulfilled.
  * Note that dependancy is fulfilled also when it exited instead of started.
  * @param {object} sender - Sender of the message
  * @param {object} params - not used
  */
  async mmStart(sender, params) {
    if(this.config.uploaddir) {
      const platform = mfs.platform()
      if(!MOSY_SERVER && (platform==='node' || platform==='nwjs')) {
        this.upload_dir = mfs.fullpath(this.config.uploaddir)
        try{
          await mfs.promises.mkpath(this.config.uploaddir)
        }catch(err) {
          if(err.code!=='EEXIST') {
            this.error('failed to create upload directory:', err.message)
          }
        }
      }
    }
    this.repos = this.config.repos = this.config.repos || {}
    await this.initRepositories(Object.keys(this.repos))
    super.mmStart(sender, params)
    if(MOSY_SERVER) setTimeout(()=>this.publish('msvrServer', 'mmAllowMessages', { actions: ALLOWED_MESSAGES_FROM_CLIENTS }), 1000)
    this.startDaemon(10000, 'cleanCacheValidTimes')
    if(this.config.memory_cache_enabled) {
      this.startDaemon(30000, 'cleanMemoryCache') // Clean memory cache every 30 seconds
    }
    if(this.config.cache_stats_enabled) {
      this.startDaemon(60000, 'cleanCacheStats') // Clean stat cache every 60 seconds
    }
  } // mmStart

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Removes all in-memory records on validity of cache expiration times
  */
  cleanCacheValidTimes() {
    const second = Math.floor(Date.now()/1000)
    for(const repo in this.repos) {
      const cfg = this.repos[repo]
      if(cfg.readonly) continue
      for(const path in cfg.cache_valid_until) {
        if(cfg.cache_valid_until[path]<second) delete cfg.cache_valid_until[path]
      }
    }
  } // cleanCacheValidTimes

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Get file from memory cache (LRU)
  * @param {string} uri - URI of the cached file
  * @returns {object|null} {data, mtime} if found and valid, null otherwise
  */
  getFromMemoryCache(uri) {
    if(!this.config.memory_cache_enabled) return null

    const cached = this.memory_cache.get(uri)
    if(!cached) return null

    const now = Math.floor(Date.now()/1000)
    const age = now - cached.last_access

    // Check if expired
    if(age > this.config.memory_cache_expires) {
      this.memory_cache.delete(uri)
      this.memory_cache_size -= cached.size
      return null
    }

    // Update LRU: delete and re-insert to move to end of Map
    this.memory_cache.delete(uri)
    cached.last_access = now
    this.memory_cache.set(uri, cached)

    return { data: cached.data, mtime: cached.mtime }
  } // getFromMemoryCache

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Save file to memory cache with LRU eviction
  * @param {string} uri - URI of the file
  * @param {string} data - File data (dataurl format)
  * @param {number} mtime - Modification time
  */
  saveToMemoryCache(uri, data, mtime) {
    if(!this.config.memory_cache_enabled) return
    if(!data || typeof data !== 'string') return

    // Estimate size (dataurl base64 is ~1.33x original, but this is close enough)
    const size = data.length

    // Skip if single item exceeds max cache size
    if(size > this.config.memory_cache_max_bytes) return

    // Evict old items if needed
    this.evictFromMemoryCache(size)

    // Add to cache
    this.memory_cache.set(uri, {
      data,
      mtime,
      size,
      last_access: Math.floor(Date.now()/1000),
    })
    this.memory_cache_size += size
  } // saveToMemoryCache

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Evict items from memory cache to make room for new item
  * @param {number} needed_size - Size needed for new item
  */
  evictFromMemoryCache(needed_size) {
    if(!this.config.memory_cache_enabled) return

    // Evict until we have room (by count or by size)
    while(this.memory_cache.size > 0) {
      const will_exceed_count = this.memory_cache.size >= this.config.memory_cache_max_items
      const will_exceed_size = (this.memory_cache_size + needed_size) > this.config.memory_cache_max_bytes

      if(!will_exceed_count && !will_exceed_size) break

      // Remove oldest item (first in Map = least recently used)
      const first_key = this.memory_cache.keys().next().value
      const first_item = this.memory_cache.get(first_key)
      this.memory_cache.delete(first_key)
      this.memory_cache_size -= first_item.size
    }
  } // evictFromMemoryCache

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Clean up expired items from memory cache
  */
  cleanMemoryCache() {
    if(!this.config.memory_cache_enabled) return

    const now = Math.floor(Date.now()/1000)
    const to_delete = []

    // Find expired items
    for(const [uri, cached] of this.memory_cache.entries()) {
      const age = now - cached.last_access
      if(age > this.config.memory_cache_expires) {
        to_delete.push(uri)
      }
    }

    // Delete expired items
    for(const uri of to_delete) {
      const cached = this.memory_cache.get(uri)
      this.memory_cache.delete(uri)
      this.memory_cache_size -= cached.size
    }

    if(to_delete.length > 0) {
      this.debug(`Memory cache cleanup: removed ${to_delete.length} expired items`)
    }
  } // cleanMemoryCache

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Get cached file stat information or fetch from filesystem
  * @param {string} cache_path - Full path to cache file
  * @param {string} uri - URI of the file (for caching key)
  * @returns {Promise<{mtime: number, exists: boolean}>}
  */
  async getCachedStats(cache_path, uri) {
    if(!this.config.cache_stats_enabled) {
      // Stat caching disabled, check filesystem directly
      let mtime = 0
      try {
        const stats = await mfs.promises.statFile(cache_path)
        mtime = _.natural(stats ? stats.mtime : 0)
      } catch(err) {
        // File doesn't exist
      }
      return { mtime, exists: mtime > 0 }
    }

    // Check if we have recent cached stats
    const cached = this.cache_stats.get(uri)
    if(cached && (Date.now() - cached.checked_at) < this.config.cache_stats_ttl) {
      // Use cached stats (skip filesystem)
      return cached
    }

    // Need to check filesystem
    let mtime = 0
    let exists = false
    try {
      const stats = await mfs.promises.statFile(cache_path)
      mtime = _.natural(stats ? stats.mtime : 0)
      exists = mtime > 0
    } catch(err) {
      // File doesn't exist
    }

    const stat_info = { mtime, exists, checked_at: Date.now() }

    // Evict old entries if needed (LRU)
    if(this.cache_stats.size >= this.config.cache_stats_max_items) {
      const first_key = this.cache_stats.keys().next().value
      this.cache_stats.delete(first_key)
    }

    // Cache the stat info
    this.cache_stats.set(uri, stat_info)

    return stat_info
  } // getCachedStats

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Clean up expired entries from stat cache
  */
  cleanCacheStats() {
    if(!this.config.cache_stats_enabled) return

    const now = Date.now()
    const to_delete = []

    // Find expired entries
    for(const [uri, cached] of this.cache_stats.entries()) {
      const age = now - cached.checked_at
      if(age > this.config.cache_stats_ttl * 2) { // Delete if older than 2x TTL
        to_delete.push(uri)
      }
    }

    // Delete expired entries
    for(const uri of to_delete) {
      this.cache_stats.delete(uri)
    }

    if(to_delete.length > 0) {
      this.debug(`Stat cache cleanup: removed ${to_delete.length} expired entries`)
    }
  } // cleanCacheStats

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Initializes list of repositories
  * @param {array} repos - list of repository names
  * @returns {Promise<void>}
  */
  async initRepositories(repos) {
    for(const repo of repos) {
      await this.initRepository(repo)
    }
  } // initRepositories

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Initializes one repository
  * @param {string} repo - repository name
  * @returns {Promise<void>}
  */
  async initRepository(repo) {
    const cfg = this.repos[repo] || {}

    cfg.path = _.toString(cfg.path).replace(/\\/g, '/').replace(/\/$/, '')
    if(cfg.path==='') cfg.path = repo
    cfg.fallbacks = cfg.fallbacks || []
    cfg.queue = ['updateRepo']
    cfg.cache = !!cfg.cache
    cfg.refresh = _.natural(cfg.refresh*1000)
    cfg.pause = _.natural(cfg.pause)
    cfg.pull = _.natural(cfg.pull)
    cfg.cache_valid_until = {}

    if(mfs.platform()==='web') return

    // Create repository directory
    try {
      await mfs.promises.mkpath(this.config.rootdir+cfg.path)
    } catch(err) {
      // Ignore mkdir errors
    }

    // Check for server listing file
    const server_listing_file = this.config.rootdir+cfg.path+'/'+SERVER_LISTING_FILE
    let stats = null
    try {
      stats = await mfs.promises.statFile(server_listing_file)
    } catch(err) {
      // File doesn't exist, that's ok
    }

    cfg.last_pull = stats ? _.natural(stats.mtime) : 0
  } // initRepository

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Listener uses this message to notify #ALL that it successfully finished starting. Normally is not invoked manually, but through this.started() method.
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {integer} [params.duration]
  */
  mmStarted(sender, params) {
    if(sender.listener_id==='msvrServer') {
      this.publish('msvrServer', 'mmAllowMessages', { actions: ALLOWED_MESSAGES_FROM_CLIENTS })
    }
  } // mmStarted

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Automatically invoked when all expected listeners were started or exited
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {integer} [params.init] - timestamp when application starts with loading listeners
  * @param {integer} [params.loaded] - timestamp when all listeners are loaded and first listener starts
  * @param {integer} [params.started] - timestamp when all listeners started
  */
  async mmStartedAll(sender, params) {
    // start syncing/cleaning of repositories
    for(const repo in this.repos) {
      setTimeout(this.actionRepo.bind(this, repo), 0)
    }

    // notify all listeners on existing files in uploads directory
    if(this.upload_dir) {
      try {
        const files = await fs.promises.readdir(this.upload_dir)
        for(const file of files) {
          const file_path = this.upload_dir+'/'+file
          this.info(`upload file detected: ${file_path}`)
          this.publish('#UPLOAD', 'mmFile', { file: file_path })
        }
      } catch(err) {
        this.error(`failed to read upload directory ${this.upload_dir}: ${err.message}`)
      }
    }
  } // mmStartedAll

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Internal method to fetch file data (with deduplication, no If-Modified-Since check)
  * @param {string} uri - identifier of the resource, usually path
  * @returns {Promise<{data?: string, mtime?: number, error?: string}>}
  */
  async _fetchFile(uri) {
    try {
      // Validate URI
      if(/\.\./.test(uri)) {
        throw new Error('Invalid URI0: '+ uri)
      }

      // Parse repo and path
      const x = uri.match(/^([^/]+)\/(.+)$/) || []
      const repo = x[1]
      const path = x[2]
      const cfg = this.repos[repo]

      if(!repo || !cfg) {
        throw new Error('Invalid URI1: ' + uri)
      }

      // Check memory cache first (fastest)
      const mem_cached = this.getFromMemoryCache(uri)
      if(mem_cached) {
        // this.debug(`Memory cache HIT: ${uri}`)
        return mem_cached
      }

      const cache_path = this.config.rootdir + cfg.path + '/' + path
      const fallbacks = _.cloneDeep(cfg.fallbacks)

      // Check cache file stats (using cached stats if available)
      const stat_info = await this.getCachedStats(cache_path, uri)
      const cache_mtime = stat_info.mtime

      // Check if cache is still valid (for readonly repos or within expires time)
      const isCacheValid = cfg.readonly || this.isCacheStillValid(cache_mtime, cfg.expires)

      if(cache_path && isCacheValid && cache_mtime > 0) {
        // Serve from disk cache
        try {
          const { data } = await this.readFromCache(cache_path, cache_mtime)
          // Save to memory cache for next time
          this.saveToMemoryCache(uri, data, cache_mtime)
          return { data, mtime: cache_mtime }
        } catch(err) {
          // Cache read failed, fall through to fallbacks
          this.warn(`Cache read failed for ${uri}, trying fallbacks`)
        }
      }

      // No fallbacks available
      if(fallbacks.length === 0) {
        throw new Error('Not found: ' + uri)
      }

      // Try fallback chain
      const result = await this.getFileUsingFallbacks(uri, cache_path, cache_mtime, fallbacks)
      const mtime = _.natural(result.mtime)

      // Save to disk cache in background (async, fire-and-forget)
      if(!cfg.readonly && mtime > 0 && (!cache_mtime || cache_mtime < mtime)) {
        this.saveToCache(uri, result.data, mtime).catch(err => {
          this.warn(`Background cache save failed for ${uri}:`, err.message)
        })
      }

      // Save to memory cache
      this.saveToMemoryCache(uri, result.data, mtime)

      // Return file data and mtime
      return { data: result.data, mtime }
    } catch(err) {
      this.error(`_fetchFile failed for ${uri}:`, err.message)
      throw err
    }
  } // _fetchFile

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * request to obtain the resource data
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.uri - identifier of the resource, usually path
  * @param {integer} [params.mtime] - if set, mmNotModified will be returned instead of mmFile
  */
  async mmFileGet(sender, params) {
    const uri = params.uri
    const if_modified_since = _.natural(params.mtime)

    try {
      // Check if there's already a pending request for this URI
      let pending = this.pending_requests.get(uri)

      if(!pending) {
        // No pending request, create a new one
        pending = this._fetchFile(uri)
        this.pending_requests.set(uri, pending)

        // Clean up when done (success or failure)
        pending.finally(() => {
          this.pending_requests.delete(uri)
        }).catch(() => {
          // Ignore errors here - they're handled by the code awaiting the original Promise
        })
      }

      // Wait for the result (either new or shared request)
      const result = await pending

      // Check If-Modified-Since (each client checks independently)
      if(if_modified_since > 0 && result.mtime > 0 && if_modified_since >= result.mtime) {
        return this.reply(sender, 'mmNotModified')
      }

      // Return file data
      this.reply(sender, 'mmFile', { data: result.data, mtime: result.mtime })
    } catch(err) {
      this.error(`mmFileGet failed for ${uri}:`, err.message)
      this.reply(sender, 'mmFailure', { error: err.message })
    }
  } // mmFileGet

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Check if cache is still valid based on modification time and expires config
  * @param {number} cache_mtime - cache file modification time
  * @param {number} expires - cache validity duration in seconds
  * @returns {boolean}
  */
  isCacheStillValid(cache_mtime, expires) {
    if(!cache_mtime || !expires) return false
    const age = Math.floor(Date.now()/1000) - cache_mtime
    return age < expires
  } // isCacheStillValid

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Get file from peering app's msysProxy
  * @param {string} uri - URI of the request
  * @param {number} cache_mtime - modification time of cache file (for If-Modified-Since)
  * @returns {Promise<{data: string, mtime: number}>}
  */
  async getFromPeering(uri, cache_mtime) {
    if(!this.peering_app_id) {
      throw new Error('No peering connection available')
    }

    // this.debug(`Trying peering fallback for: ${uri}`)
    const req_params = { uri }
    if(cache_mtime>0) req_params.mtime = cache_mtime

    const { action, params } = await this.request(
      this.peering_app_id+'@msysProxy',
      'mmFileGet',
      req_params,
      ['mmFile', 'mmNotModified'],
    )

    if(action==='mmNotModified') {
      throw new Error('NOT_MODIFIED') // signal to use cache
    }

    // this.debug(`✓ Got file from peering: ${uri}`)
    return { data: params.data, mtime: params.mtime }
  } // getFromPeering

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Get file from server's msysProxy
  * @param {string} uri - URI of the request
  * @param {number} cache_mtime - modification time of cache file (for If-Modified-Since)
  * @returns {Promise<{data: string, mtime: number}>}
  */
  async getFromServer(uri, cache_mtime) {
    // If not connected and this is not the last fallback, fail fast
    if(!this.server_connected) {
      throw new Error('Server not connected')
    }

    // this.debug(`Trying server fallback for: ${uri}`)
    const req_params = { uri }
    if(cache_mtime>0) req_params.mtime = cache_mtime

    const { action, params } = await this.request(
      ':msysProxy',
      'mmFileGet',
      req_params,
      { resolve: ['mmFile', 'mmNotModified'], timeout: this.config.server_request_timeout },
    )

    if(action==='mmNotModified') {
      throw new Error('NOT_MODIFIED') // signal to use cache
    }

    // this.debug(`✓ Got file from server: ${uri}`)
    return { data: params.data, mtime: params.mtime }
  } // getFromServer

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Get file from external HTTP URL
  * @param {string} fallback - URL template with placeholders
  * @param {string} uri - URI of the request
  * @param {number} cache_mtime - modification time of cache file (for If-Modified-Since)
  * @returns {Promise<{data: string, mtime: number}>}
  */
  async getFromHttp(fallback, uri, cache_mtime) {
    const path = uri.replace(/^.*?\//, '')
    const pathnoext = path.replace(/\.\w+$/, '')

    // Extract tile coordinates for map tile URLs (e.g., map_osm/18/141234/87654.png)
    const tileMatch = uri.match(/\/(\d+)\/(\d+)\/(\d+)\./)
    const z = tileMatch ? tileMatch[1] : ''
    const x = tileMatch ? tileMatch[2] : ''
    const y = tileMatch ? tileMatch[3] : ''

    // Replace {s} with random subdomain (for tile servers)
    const subdomains = ['a', 'b', 'c']
    const s = subdomains[Math.floor(Math.random() * subdomains.length)]

    const url = fallback
      .replace(/{uri}/g, uri)
      .replace(/{path}/g, path)
      .replace(/{pathnoext}/g, pathnoext)
      .replace(/{z}/g, z)
      .replace(/{x}/g, x)
      .replace(/{y}/g, y)
      .replace(/{s}/g, s)

    // this.debug(`Trying HTTP fallback: ${url}`)

    return new Promise((resolve, reject) => {
      mfs.readFile(url, { type: 'dataurl', mtime: cache_mtime }, (err, data, mtime) => {
        if(err) {
          if(!/http:\/\/maps/.test(url)) this.warn(`HTTP fetch failed "${url}": ${err}`)
          return reject(new Error(`HTTP ${err}`))
        }

        if(!data) {
          // Server returned 304 Not Modified
          return reject(new Error('NOT_MODIFIED'))
        }

        mtime = mtime>0 ? mtime : parseInt(Date.now()/1000)
        // this.debug(`✓ Got file from HTTP: ${url}`)
        resolve({ data, mtime })
      })
    })
  } // getFromHttp

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Read file from local cache
  * @param {string} cache_path - path to cache file
  * @param {number} cache_mtime - modification time of cache file
  * @returns {Promise<{data: string, mtime: number}>}
  */
  async readFromCache(cache_path, cache_mtime) {
    try {
      const data = await mfs.promises.readFile(cache_path, 'dataurl')
      // this.debug(`✓ Serving from cache: ${cache_path}`)
      return { data, mtime: cache_mtime }
    } catch(err) {
      throw new Error(`Cache read failed: ${err}`)
    }
  } // readFromCache

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Retrieve resource using fallback chain
  * @param {string} uri - URI of the request
  * @param {string} cache_path - path to local cache file
  * @param {number} cache_mtime - modification time of the cache file
  * @param {array} fallbacks - list of fallbacks to try
  * @returns {Promise<{data: string, mtime: number}>}
  */
  async getFileUsingFallbacks(uri, cache_path, cache_mtime, fallbacks) {
    // Try each fallback in order
    for(const fallback of fallbacks) {
      try {
        if(fallback === '@') {
          return await this.getFromPeering(uri, cache_mtime)
        } else if(fallback === ':') {
          return await this.getFromServer(uri, cache_mtime)
        } else {
          return await this.getFromHttp(fallback, uri, cache_mtime)
        }
      } catch(err) {
        // Special case: NOT_MODIFIED means use cache
        if(err.message === 'NOT_MODIFIED') {
          if(cache_path && cache_mtime > 0) {
            return await this.readFromCache(cache_path, cache_mtime)
          }
        }

        // this.debug(`✗ Fallback ${fallback} failed: ${err.message}`)
        // Continue to next fallback
      }
    }

    // All fallbacks failed, try to return cached file if available
    if(cache_path && cache_mtime > 0) {
      // this.debug(`All fallbacks failed, using cache for: ${uri}`)
      return await this.readFromCache(cache_path, cache_mtime)
    }

    throw new Error(`Not found: ${uri}`)
  } // getFileUsingFallbacks

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * save data to cache file
  * @param {string} uri - URI of the request
  * @param {string} data - data in dataurl format
  * @param {number} mtime - how to set last modified stamp on the file; 0 to set for current time
  */
  async saveToCache(uri, data, mtime) {
    if(mfs.platform()==='web') return // no cache possible for web app
    const x = uri.match(/^([^/]+)\/(.*)$/)
    if(!x) return
    const repo = x[1]; const path = x[2]
    const cfg = this.repos[repo]
    if(!cfg) return
    if((cfg.cache || cfg.pull) && /^data:/.test(data) && !/^data:[^,]*,$/.test(data)) {
      const valid =
      /\.jpe?g$/i.test(uri) ? mfs.validJPG(data) :
        /\.png$/i.test(uri) ? mfs.validPNG(data) :
          true
      if(!valid) return this.error(`invalid image not stored in the cache: ${uri} (${data.length} bytes)`)
      const local_path = this.config.rootdir+cfg.path+'/'+path
      if(this.just_saving[local_path]) return
      this.just_saving[local_path] = true
      try {
        await mfs.promises.writeFile(local_path, data, 'dataurl')
        mfs.touchFile(local_path, mtime)
        delete this.just_saving[local_path]
        // Invalidate stat cache so next request gets fresh stats
        this.cache_stats.delete(uri)
      } catch (error) {
        delete this.just_saving[local_path]
      }
    }
  } // saveToCache

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysPeeringClient informs all listeners that connection to peering is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} [params.url] - URL of connected peering
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the peering
  */
  mmPeeringConnected(sender, params) {
    this.peering_app_id = params.app_id
  } // mmPeeringConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysPeeringClient informs all listeners that connection to peering has dropped
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.reason] - reason for close
  * @param {string} [params.url] - URL of disconnected peering
  */
  mmPeeringDisconnected(sender, params) {
    this.peering_app_id = ''
  } // mmPeeringDisconnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - app_id of the server
  * @param {string} params.url - URL of connected server
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the server
  */
  mmServerConnected(sender, params) {
    this.server_connected = true
  } // mmServerConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer has dropped
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.reason] - reason for close
  * @param {string} params.url - URL of disconnected server
  */
  mmServerDisconnected(sender, params) {
    this.server_connected = false
  } // mmServerDisconnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * perform action from repository's action queue
  */
  actionRepo(repo) {
    const cfg = this.repos[repo]
    if(!cfg) return

    // we do the call to actionRepo through setTimeout to prevent stack overflow
    const nextAction = (ms)=>{
      ms = _.natural(ms) || cfg.pause
      setTimeout(this.actionRepo.bind(this, repo), ms)
    }

    if(cfg.pull>0) {
      // repository acts as copy of the server's repo

      if(this.server_connected && (Date.now()/1000 - cfg.last_pull > cfg.pull) && cfg.queue[0]!=='pullRepo') {
        // we are online and current listing from server is too old, ask for refresh
        cfg.queue.unshift('pullRepo')
      }
    }

    if(cfg.queue.length===0) {
      // nothing to do, plan compare current files with latest listing from server in configured interval
      cfg.queue = ['updateRepo']
      return nextAction(cfg.refresh)
    }

    // perform action from the queue
    const queue_item = _.toString(cfg.queue.shift()).match(/^(\w+)(?::(.*))?/) || []
    const action = _.toString(queue_item[1])
    const file = _.toString(queue_item[2])
    if(typeof (this[action])!=='function') return nextAction(1)
    setTimeout(this[action].bind(this, nextAction, repo, file), cfg.pause)
  } // actionRepo

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * get list of files from remote repository
  */
  async pullRepo(nextAction, repo) {
    const cfg = this.repos[repo]
    cfg.last_pull = _.natural(Date.now()/1000) // regardless of result, treat this as last pull to not repeat sooner than cfg.pull interval
    const server_listing_file = this.config.rootdir+cfg.path+'/'+SERVER_LISTING_FILE

    try {
      // Read current listing
      let json = null
      try {
        json = await mfs.promises.readFileJSON(server_listing_file)
      } catch(err) {
        // File doesn't exist or invalid JSON, that's ok
      }

      const p = { uri: repo }
      if(json) p.md5 = mcrypt.md5(JSON.stringify(json)) // provide MD5 hash of our version of listing, to prevent sending on no change

      const { params } = await this.request(':msysProxy', 'mmFileListGet', p, 'mmFileList')
      if(params.files) {
        // received list of current files on server
        await mfs.promises.writeFile(server_listing_file, JSON.stringify(params.files), 'text')
        cfg.queue = ['updateRepo'] // remove all planned actions and ask for recomparison with new listing from server
      }
    } catch(err) {
      // Request failed or write failed, that's ok
    }

    nextAction()
  } // pullRepo

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * get list of files from remote repository
  */
  updateRepo(nextAction, repo) {
    const cfg = this.repos[repo]

    // update differs for cached repos and sync repos
    if(cfg.pull>0) {
      return this.diffRepo(nextAction, repo)
    }else{
      return this.cleanRepo(nextAction, repo)
    }
  } // updateRepo

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * clean cached repository
  */
  cleanRepo(nextAction) {
    nextAction()
  } // cleanRepo

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * compare repository with stored listing of server files and append actions to make it in sync to working queue
  */
  async diffRepo(nextAction, repo) {
    const cfg = this.repos[repo]
    if(cfg.readonly) return nextAction()

    try {
      const server_listing_file = this.config.rootdir+cfg.path+'/'+SERVER_LISTING_FILE
      const server_file_content = await mfs.promises.readFile(server_listing_file)
      const server_entries = JSON.parse(server_file_content)
      const local_entries = await this.listFiles(cfg.path)

      // we expect both local and server list are sorted alphabetically
      let local_entry = local_entries.shift()
      let server_entry = server_entries.shift()
      while(local_entry || server_entry) {
        if(local_entry && (!server_entry || local_entry[0] < server_entry[0])) {
          // local entry not in server listing
          // this.debug('to be deleted '+local_entry[0])
          cfg.queue.push('deleteFile:'+local_entry[0])
          local_entry = local_entries.shift()
        }else if(server_entry && (!local_entry || local_entry[0] > server_entry[0])) {
          // server entry not in local listing
          // this.debug('to be downloaded '+server_entry[0])
          if(server_entry[1]<0) {
            cfg.queue.push('makeDir:'+server_entry[0])
          }else {
            cfg.queue.push('downloadFile:'+server_entry[0])
          }
          server_entry = server_entries.shift()
        }else if(local_entry && server_entry && local_entry[0]===server_entry[0]) {
          const path = local_entry[0]
          const local_dir = local_entry[1]<0
          const server_dir = server_entry[1]<0
          if(local_dir && server_dir) {
            // both are directories, nothing to do
          }else if(local_dir !== server_dir) {
            // directory changed to file or vice versa, must be destroyed and redownloaded
            // this.debug('to be deleted+downloaded '+path)
            cfg.queue.push('deleteFile:'+path)
            cfg.queue.push('downloadFile:'+path)
          }else{
            // both are files
            if(local_entry[1]!==server_entry[1] || local_entry[2]!==server_entry[2]) {
              // this.debug('to be re-downloaded '+path)
              cfg.queue.push('downloadFile:'+path)
            }
          }
          local_entry = local_entries.shift()
          server_entry = server_entries.shift()
        }else{
          this.error('problem matching listings')
        }
      }
    } catch(err) {
      cfg.last_pull = 0
    }

    nextAction()
  } // diffRepo

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * delete file from repository
  */
  deleteFile(nextAction, repo, file) {
    // this.debug('deleteFile: '+repo+': '+file)
    const cfg = this.repos[repo] || {}
    mfs.delete(this.config.rootdir+cfg.path+'/'+file, ()=>{ nextAction() })
  } // deleteFile

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * download file from server to local repository
  */
  async downloadFile(nextAction, repo, file) {
    const cfg = this.repos[repo]
    try {
      const result = await this.getFileUsingFallbacks(repo+'/'+file, null, 0, _.cloneDeep(cfg.fallbacks))
      await this.saveToCache(repo+'/'+file, result.data, result.mtime)
    } catch(err) {
      this.warn(`Failed to download ${repo}/${file}:`, err.message)
    }
    nextAction()
  } // downloadFile

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * download file from server to local repository
  */
  makeDir(nextAction, repo, file) {
    // this.debug('makeDir: '+repo+': '+file)
    const cfg = this.repos[repo] || {}
    mfs.mkpath(this.config.rootdir+cfg.path+'/'+file, ()=>{ nextAction() })
  } // makeDir

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * request to obtain the list of files
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.uri - identifier of the resource, usually path to directory
  * @param {string} [params.md5] - if md5 is provided, list will be sent only if there was a change
  */
  async mmFileListGet(sender, params) {
    const uri = params.uri
    if(/\.\./.test(uri)) return this.reply(sender, 'mmFailure', { error: 'Invalid URI2' })

    if(mfs.platform()==='web') {
      // list remote files
      const reply = await this.request(':msysProxy', 'mmFileListGet', { uri }, { reject: [] })
      this.reply(sender, reply.action, reply.params)
    }else{
      // list local files
      const x = uri.match(/^([^/]+)(?:\/(.+))?$/) || []
      const repo = x[1]
      const path = _.toString(x[2])
      const cfg = this.repos[repo]
      if(!repo || !cfg) return this.reply(sender, 'mmFailure', { error: 'Invalid URI3' })

      const relative_path = cfg.path+'/'+path
      const entries = await this.listFiles(relative_path)

      if(params.md5 && params.md5===mcrypt.md5(JSON.stringify(entries))) {
        this.reply(sender, 'mmFileList', { uri })
      }else {
        this.reply(sender, 'mmFileList', { uri, files: entries })
      }
    }
  } // mmFileListGet

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * returns list of all files and directories below given proxy path
  * @param {string} relative_path - relative path to search
  * @returns {Promise<Array>} Promise resolving to array of entries as tuples [path,size,mtime]
  */
  async listFiles(relative_path) {
    let root = this.config.rootdir+relative_path
    root = root.replace(/\/$/, '')
    const skiplen = root.length+1

    return new Promise((resolve) => {
      mfs.find(root, {
        exclude: '\\.DS_Store$|'+SERVER_LISTING_FILE.replace(/\./, '\\.'),
        finish: entries=>{
          entries.forEach(entry=>{
            entry[0] = entry[0].substr(skiplen)
          })
          entries = entries.sort((a, b)=>{ return(a[0]<b[0]?-1 : 1) })
          resolve(entries)
        },
      })
    })
  } // listFiles

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Chunk of data
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.file] - path of the file
  * @param {object} [params.bin] - chunk of binary data
  * @param {integer} [params.offset] - offset of the chunk in the whole file
  * @param {integer} [params.total] - total size of the file
  * @param {string} [params.md5] - md5 checksum of the whole file
  */
  async mmChunk(sender, params) {
    let { file, bin, offset, total, md5 } = params
    file = String(file ?? '')
    offset = parseInt(offset)
    if(offset < 0) offset = NaN
    total = parseInt(total)
    if(total < 0) total = NaN
    md5 = String(md5 ?? '')
    if(!(bin instanceof Uint8Array)) bin = null

    let tmp_file_path = null

    const fail = async (error) => {
      if(tmp_file_path) {
        try {
          await fs.promises.unlink(tmp_file_path)
        } catch(err) {
          // Ignore unlink errors
        }
      }
      this.error(`mmChunk: ${error}`)
      this.replyIfRequest(sender, 'mmFailure', { error })
    }

    if(!this.upload_dir) {
      return await fail('no upload dir configured')
    }

    const platform = mfs.platform()
    if(platform !== 'nwjs' && platform !== 'node') {
      return await fail(`not allowed on ${platform} platform`)
    }
    if(MOSY_SERVER) {
      return await fail('not allowed on mServer')
    }

    if(file === '') {
      return await fail('missing file parameter')
    }
    if(!/^[a-zA-Z0-9_\-.]+$/.test(file)) {
      return await fail('invalid file parameter')
    }

    const file_path = this.upload_dir + '/' + file
    tmp_file_path = file_path + '.part'

    if(bin === null && isNaN(offset) && isNaN(total) && !md5) {
      // request to delete file
      try {
        await fs.promises.unlink(file_path)
      } catch(err) {
        // Ignore unlink errors
      }
      try {
        await fs.promises.unlink(tmp_file_path)
      } catch(err) {
        // Ignore unlink errors
      }
      return this.replyIfRequest(sender, 'mmSuccess')
    }

    // if there are binary data, offset must be provided
    if(bin !== null && isNaN(offset)) {
      return await fail('missing offset parameter')
    }

    // save incoming chunk
    if(bin !== null) {
      // Append chunk to temp file
      try{
        if(offset === 0) {
        // starting new file, remove any existing temp file
          try {
            await fs.promises.unlink(tmp_file_path)
          } catch(err) {
          // Ignore unlink errors
          }
        }

        // check current size of temp file
        let current_size = 0
        try {
          const stat = await fs.promises.stat(tmp_file_path)
          current_size = parseInt(stat.size)
        } catch(err) {
        // File doesn't exist
        }

        if(current_size !== offset) {
          return await fail(`invalid offset: expected ${current_size}, got ${offset}`)
        }

        // append data
        await fs.promises.appendFile(tmp_file_path, Buffer.from(bin))
      } catch(err) {
        return await fail(`file write error: ${err.message}`)
      }
    }

    if(bin === null || bin.length === 0) {
      // no data provided; could be finalisation of the upload
      if(isNaN(total) && !md5) {
        return await fail('missing total or md5 parameter for finalisation')
      }

      // check final file size
      if(!isNaN(total)) {
        let stats = null
        try {
          stats = await fs.promises.stat(tmp_file_path)
        } catch(err) {
          return await fail('file not found for size check')
        }
        const file_size = parseInt(stats.size)
        if(file_size !== total) {
          return await fail(`file size mismatch: expected ${total}, got ${file_size}`)
        }
      }

      // check final file MD5
      if(md5) {
        let file_md5 = ''
        try {
          file_md5 = await md5File(tmp_file_path)
        } catch(err) {
          return await fail('file not found for md5 check')
        }
        if(file_md5 !== md5) {
          return await fail('md5 checksum mismatch')
        }
      }

      try{
        await fs.promises.rename(tmp_file_path, file_path)
      } catch(err) {
        return await fail(`file rename error: ${err.message}`)
      }
      this.info(`new file uploaded: ${file_path}`)
      this.publish('#UPLOAD', 'mmFile', { file: file_path })
    }

    return this.reply(sender, 'mmSuccess')
  } // mmChunk
} // msysProxy
